import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import { Alert, AlertTitle, Box, Paper, Stack } from "@mui/material";
import {
  type DigitalSample,
  useDigitalSample,
  useDigitalSampleGalleryImages,
} from "hooks/digital-samples";
import { type FC, useState } from "react";
import { DigitalSampleCard } from "./DigitalSampleCard";
import { DigitalSampleCardSkeleton } from "./DigitalSampleCardSkeleton";
import { DigitalSampleImageGallery } from "./DigitalSampleImageGallery";
import { DigitalSampleRequestButton } from "./digitalSampleRequest/DigitalSampleRequestButton";
import { RequestedDigitalSamples } from "./RequestedDigitalSamples";

export interface DigitalSamplesProps {
  material: ColoredMaterial;
  isInternal?: boolean;
}

export const DigitalSamples: FC<DigitalSamplesProps> = ({
  material,
  isInternal,
}) => {
  const [digitalSamples, error, loading] = useDigitalSample(
    { eljExperimentId: material.eljExperimentId },
    material.digitalSample.hasDigitalSample,
  );
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [fetchDigitalSampleRequests, setFetchDigitalSampleRequests] =
    useState(0);

  const images = useDigitalSampleGalleryImages(digitalSamples);

  const openImageGallery = (digitalSample: DigitalSample) => {
    const imageIdx = images.findIndex(
      (img) => img.digitalSample.id === digitalSample.id,
    );
    if (imageIdx === -1) return;
    setOpen(true);
    setSelectedImage(imageIdx);
  };

  /**
   * DISA-1433:  https://covestro.atlassian.net/browse/DISA-1433
   * Expected Behavior : Only published textures must appear
   * */
  const sampleList = isInternal
    ? digitalSamples
    : digitalSamples?.filter((sample) => sample.isPublished);

  return (
    <>
      <Stack spacing={2}>
        {error && (
          <Paper variant="outlined">
            <Alert severity="error">
              <AlertTitle>Failed to load digital samples</AlertTitle>
              {error.message}
            </Alert>
          </Paper>
        )}
        {loading && <DigitalSampleCardSkeleton />}
        {sampleList?.map((digitalSample, idx) => (
          <DigitalSampleCard
            key={idx}
            onZoom={() => openImageGallery(digitalSample)}
            value={digitalSample}
            material={material}
            isInternal={isInternal}
          />
        ))}
        {(!material.digitalSample.hasDigitalSample ||
          !sampleList?.length ||
          images?.length === 0) && (
          <Paper variant="outlined">
            <Alert severity="info">
              <Box>
                There are no Digital Samples available for this color / material
                combination.
                <RequestedDigitalSamples
                  key={fetchDigitalSampleRequests}
                  material={material}
                />
              </Box>
              <DigitalSampleRequestButton
                material={material}
                sx={{ marginTop: 1 }}
                onDigitalSampleRequestSubmit={() =>
                  setFetchDigitalSampleRequests(Date.now)
                }
              />
            </Alert>
          </Paper>
        )}
        {isInternal &&
          material.availability.colorStatus === "ACTIVE" &&
          material.digitalSample.hasDigitalSample &&
          images?.length !== 0 && (
            <Paper variant="outlined">
              <Alert severity="info" icon={<HelpOutlineSharpIcon />}>
                <Box>
                  Looking for a different Texture or Finish?
                  <RequestedDigitalSamples
                    key={fetchDigitalSampleRequests}
                    material={material}
                  />
                </Box>
                <DigitalSampleRequestButton
                  material={material}
                  sx={{ marginTop: 1 }}
                  onDigitalSampleRequestSubmit={() =>
                    setFetchDigitalSampleRequests(Date.now)
                  }
                />
              </Alert>
            </Paper>
          )}
      </Stack>
      <DigitalSampleImageGallery
        images={images}
        selectedImage={selectedImage}
        open={open}
        onSelectedImageChange={setSelectedImage}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
